import { useIonToast } from '@ionic/react';
import { useEffect, useState } from 'react';
import { lastValueFrom } from 'rxjs';
import {
  ISettingsVisibility,
  IUserSettings,
  IUserSettingsResponse,
  PlayerService,
  UserSettingKey,
} from 'services/player';
import { toastConfig } from '../../config/ToastConfig';
import { NOTIFICATION_DEFAULTS } from './notificationOptions';
import { toCompareInsights } from './settingsOptions';
import { SETTINGS_VISIBILITY_DEFAULTS, resolveSettingsVisibility } from './settingsVisibility';

// Shared between the Settings list and its option pages, so a change made on an
// option page is already showing when the user goes back.
interface UserSettingsState {
  settings: IUserSettings | null;
  defaults: IUserSettings | null;
  // Server-side show/hide per Settings row (SETTINGS_ROW_VISIBILITY).
  visibility: ISettingsVisibility;
  isLoading: boolean;
}

let state: UserSettingsState = {
  settings: null,
  defaults: null,
  visibility: SETTINGS_VISIBILITY_DEFAULTS,
  isLoading: false,
};
const listeners = new Set<(next: UserSettingsState) => void>();

const setState = (patch: Partial<UserSettingsState>) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener(state));
};

// Fills in anything an older server or stored record lacks, so pages can rely
// on every field being present.
const normalize = (values: IUserSettings): IUserSettings => ({
  ...NOTIFICATION_DEFAULTS,
  ...values,
  compareInsights: toCompareInsights(values.compareInsights),
  notificationChannels: {
    ...NOTIFICATION_DEFAULTS.notificationChannels,
    ...values.notificationChannels,
  },
  newsAndUpdates: { ...NOTIFICATION_DEFAULTS.newsAndUpdates, ...values.newsAndUpdates },
  allSlotsNotifications: {
    ...NOTIFICATION_DEFAULTS.allSlotsNotifications,
    ...values.allSlotsNotifications,
  },
  favoriteSlotsNotifications: {
    ...NOTIFICATION_DEFAULTS.favoriteSlotsNotifications,
    ...values.favoriteSlotsNotifications,
  },
  playlistSlotsNotifications: {
    ...NOTIFICATION_DEFAULTS.playlistSlotsNotifications,
    ...values.playlistSlotsNotifications,
  },
});

const applyResponse = (response: IUserSettingsResponse | void) => {
  if (response?.settings) {
    setState({
      settings: normalize(response.settings),
      defaults: response.defaults ? normalize(response.defaults) : null,
      visibility: resolveSettingsVisibility(response.visibility),
    });
  }
};

const playerService = new PlayerService();

export const useUserSettings = ({ refresh = false } = {}) => {
  const [current, setCurrent] = useState(state);
  const [presentToast] = useIonToast();

  const showError = (message: string) => {
    void presentToast({ ...toastConfig, color: 'danger', message });
  };

  useEffect(() => {
    listeners.add(setCurrent);
    setCurrent(state);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);

  useEffect(() => {
    if ((!refresh && state.settings) || state.isLoading) return;
    setState({ isLoading: true });
    lastValueFrom(playerService.GetUserSettings())
      .then(applyResponse)
      .catch(() => showError('Could not load your settings.'))
      .finally(() => setState({ isLoading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  // Optimistic: the new value shows straight away and rolls back if the save fails.
  const updateSetting = <K extends UserSettingKey>(key: K, value: IUserSettings[K]) => {
    const previous = state.settings;
    if (!previous || previous[key] === value) return;
    setState({ settings: { ...previous, [key]: value } });
    lastValueFrom(playerService.SaveUserSettings({ [key]: value }))
      .then(applyResponse)
      .catch(() => {
        setState({ settings: previous });
        showError('Could not save your setting. Please try again.');
      });
  };

  // No keys → restore every setting to its default.
  const resetSettings = (keys?: UserSettingKey[]) => {
    const previous = state.settings;
    if (previous && state.defaults) {
      const defaults = state.defaults;
      const next = { ...previous };
      (keys ?? (Object.keys(defaults) as UserSettingKey[])).forEach((key) => {
        (next as Record<UserSettingKey, unknown>)[key] = defaults[key];
      });
      setState({ settings: next });
    }
    lastValueFrom(playerService.ResetUserSettings(keys))
      .then(applyResponse)
      .catch(() => {
        setState({ settings: previous });
        showError('Could not reset your settings. Please try again.');
      });
  };

  return { ...current, updateSetting, resetSettings };
};
